import { useEffect, useRef, useState } from "react";
import { useApi } from "@ncp/api-client";
import { confirmAnswer, notifySaveFailed } from "@ncp/ui";
import { repoPaths } from "./api";
import { useBuilder } from "./store";
import type { Graph, GraphIssue } from "./types";

/** Server validation of the graph on screen, debounced while editing. */
export function useLiveValidation(graph: Graph, delay = 600) {
  const api = useApi();
  const [issues, setIssues] = useState<GraphIssue[]>([]);
  useEffect(() => {
    let cancelled = false;
    const id = window.setTimeout(() => {
      api.post<{ issues: GraphIssue[] }>("/api/workflows/builder/validate", { graph }).then((r) => { if (!cancelled) setIssues(r.issues); }).catch(() => {});
    }, delay);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, [api, graph, delay]);
  return issues;
}

/**
 * Autosaves the canvas as the person's working copy of the branch (uncommitted work survives a reload). "Draft saved" shows only after the
 * API confirmed the write; a failed autosave clears it and is reported once with its cause (not again on every keystroke while it keeps
 * failing), until an autosave goes through again.
 */
export function useAutosave(repoKey: string, branch: string, baseCommitId: string, enabled: boolean) {
  const api = useApi();
  const graph = useBuilder((s) => s.graph);
  const dirty = useBuilder((s) => s.dirty);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const first = useRef(true);
  const failing = useRef(false);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (!enabled) return;
    const id = window.setTimeout(() => {
      setSaving(true);
      const request = dirty
        ? api.put(repoPaths.workingCopy(repoKey, branch), { graph, baseCommitId })
        : api.delete(repoPaths.workingCopy(repoKey, branch));
      request
        .then((answer) => { confirmAnswer(answer); setSavedAt(new Date()); failing.current = false; })
        .catch((e: unknown) => { setSavedAt(null); if (!failing.current) notifySaveFailed(e); failing.current = true; })
        .finally(() => setSaving(false));
    }, 2000);
    return () => window.clearTimeout(id);
  }, [api, repoKey, branch, baseCommitId, graph, dirty, enabled]);
  return { savedAt, saving };
}
